import slugToLocaleSlugMap from "../../slugToLocaleSlugMap.json";
import getSlugUpdates from "@utils/getUpdatedSlugs";
import { set } from "lodash";
import { LocaleCode } from "../../locales";

const isDev = process.env.NODE_ENV === "development";

export type LocaleSlugMap = Record<string, string | null>;

type TranslatableNode = {
  locale: string;
  slug?: string;
  localizations?: {
    data: ReadonlyArray<{ attributes: { locale: string; slug?: string } }>;
  };
};

/**
 * Extracts a map of locale to slug from a TranslatableNode
 *
 * Used to provide options for the language switcher
 */
export function extractLocaleSlugMap(data: TranslatableNode): LocaleSlugMap {
  const { locale, slug, localizations } = data || {};
  const localeSlugMap = { [locale]: slug || null };

  if (localizations) {
    localizations.data.forEach(({ attributes: { locale, slug } }) => {
      localeSlugMap[locale] = slug || null;
    });
  }

  return localeSlugMap;
}

type NodeType = {
  locale: string;
}[];

/**
 * Extracts a Set of unique locales from an array of nodes.
 *
 * @param nodes - An array of nodes each having a 'locale' property.
 * @returns A Set of unique locales.
 */
export const extractLocales = (nodes: NodeType) => {
  return new Set(nodes.map(node => node.locale));
};

export function updateSlugsToLocale(obj: any, locale: string) {
  if (locale === "en") return;
  if (!isLocaleCode(locale)) {
    console.error(
      `updateSlugsToLocale: The provided locale: ${locale} is not a supported locale`
    );
    return;
  }

  const entries = Object.entries(slugToLocaleSlugMap);

  const entriesWithTrailingSlash = entries.map(([slug, localeSlug]) => [
    [slug + "/", localeSlug],
    [slug, localeSlug],
  ]);

  const slugToLocaleSlugMapWithTrailingSlash = Object.fromEntries(
    entriesWithTrailingSlash.flatMap(([key, value]) => [key, value])
  );

  const slugUpdates = getSlugUpdates(
    obj,
    slugToLocaleSlugMapWithTrailingSlash,
    locale
  );

  for (const [path, updatedValue] of Object.entries(slugUpdates)) {
    set(obj, path, updatedValue);
  }
}

/** Removes <span translate="no"> tags (that we use to guide DeepL translations) */
export const stripI18nTags = (str: string): string =>
  str.replace(/<span translate="no">(.*?)<\/span>/g, "$1");

/** Gets the absolute path to the frontpage
 * @param locale - locale identifier
 */
export const getFrontpage = (locale: string) => {
  return locale === "en" ? "/" : `/${locale}/`;
};

type Language = { local: string; english: string; flag: string };

const languages: Record<LocaleCode, Language> = {
  en: { local: "English", english: "English", flag: "GB.svg" },
  de: { local: "Deutsch", english: "German", flag: "DE.svg" },
  es: { local: "Español", english: "Spanish", flag: "ES.svg" },
  fr: { local: "Français", english: "French", flag: "FR.svg" },
  it: { local: "Italiano", english: "Italian", flag: "IT.svg" },
  ja: { local: "日本語", english: "Japanese", flag: "JP.svg" },
  nl: { local: "Nederlands", english: "Dutch", flag: "NL.svg" },
  pl: { local: "Polski", english: "Polish", flag: "PL.svg" },
  pt: { local: "Português", english: "Portuguese", flag: "PT.svg" },
  sv: { local: "Svenska", english: "Swedish", flag: "SE.svg" },
  tr: { local: "Türkçe", english: "Turkish", flag: "TR.svg" },
  zh: { local: "中文", english: "Chinese", flag: "CN" },
  bg: { local: "Български", english: "Bulgarian", flag: "BG.svg" },
  cs: { local: "Čeština", english: "Czech", flag: "CZ.svg" },
  da: { local: "Dansk", english: "Danish", flag: "DK.svg" },
  el: { local: "Ελληνικά", english: "Greek", flag: "GR.svg" },
  et: { local: "Eesti", english: "Estonian", flag: "EE.svg" },
  fi: { local: "Suomi", english: "Finnish", flag: "FI.svg" },
  hu: { local: "Magyar", english: "Hungarian", flag: "HU.svg" },
  id: { local: "Indonesia", english: "Indonesian", flag: "ID.svg" },
  lt: { local: "Lietuvių", english: "Lithuanian", flag: "LT.svg" },
  lv: { local: "Latviešu", english: "Latvian", flag: "LV.svg" },
  ro: { local: "Română", english: "Romanian", flag: "RO.svg" },
  sk: { local: "Slovenčina", english: "Slovak", flag: "SK.svg" },
  sl: { local: "Slovenščina", english: "Slovenian", flag: "SI.svg" },
  uk: { local: "Українська", english: "Ukrainian", flag: "UA.svg" },
  ko: { local: "한국어", english: "Korean", flag: "KR.svg" },
  nb: { local: "Bokmål", english: "Norwegian", flag: "NO.svg" },
};

export const isLocaleCode = (locale: string): locale is LocaleCode => {
  // eslint-disable-next-line no-prototype-builtins
  return (languages as Record<string, Language>).hasOwnProperty(locale);
};

/**
 * Get local- and english language name plus flag based on the provided locale.
 *
 * @param locale - The ISO 639-1: two-letter locale code for the desired language.
 * @returns The language information object if the locale is valid; otherwise, undefined.
 * @throws Error if the locale is not valid and the application is in development mode.
 *
 */
export const getLocalLanguage = (locale: string) => {
  if (isLocaleCode(locale)) {
    return languages[locale];
  } else {
    if (isDev) {
      throw new Error(`locale: ${locale} not found in getLanguage`);
    } else {
      return { local: "NO TRANSLATION", english: "NO TRANSLATION", flag: "" };
    }
  }
};
